/**
 * Marketing Email Template Links
 *
 * Normalizes website and social inputs for marketing email templates.
 *
 * Primary responsibilities:
 * - Accept bare website domains and full http(s) links
 * - Accept social usernames or platform-specific full links
 * - Reject malformed handles and wrong social domains
 *
 * Dependencies:
 * - URL
 */

export type MarketingSocialPlatform = 'instagram' | 'linkedin' | 'x'

const SOCIAL_HOSTS = {
  instagram: new Set(['instagram.com', 'www.instagram.com']),
  linkedin: new Set(['linkedin.com', 'www.linkedin.com']),
  x: new Set(['x.com', 'www.x.com', 'twitter.com', 'www.twitter.com'])
} as const

// --------------

export function readSafeHttpUrl(value: string) {
  const trimmed = value.trim()

  if (!trimmed) return ''

  try {
    const url = new URL(trimmed)
    return url.protocol === 'https:' || url.protocol === 'http:' ? url.toString() : ''
  } catch {
    return ''
  }
}

// --------------

export function readUrlWithOptionalScheme(value: string) {
  const trimmed = value.trim()

  if (!trimmed) {
    return ''
  }

  if (/^[a-z][a-z0-9+.-]*:\/\//i.test(trimmed)) {
    return readSafeHttpUrl(trimmed)
  }

  if (/^(?:www\.)?[a-z0-9][a-z0-9.-]*\.[a-z]{2,}(?:[:/?#]|$)/i.test(trimmed)) {
    return readSafeHttpUrl(`https://${trimmed}`)
  }

  return ''
}

// --------------

export function normalizeWebsiteUrl(value: string) {
  return readUrlWithOptionalScheme(value)
}

// --------------

function normalizeSocialUsername(value: string) {
  return value.trim().replace(/^@+/, '').replace(/^\/+/, '')
}

// --------------

function isValidSocialHandle(platform: MarketingSocialPlatform, value: string) {
  if (platform === 'instagram') {
    return (
      /^[a-z0-9._]{1,30}$/i.test(value) &&
      !value.includes('..') &&
      !value.endsWith('.')
    )
  }

  if (platform === 'x') {
    return /^[a-z0-9_]{1,15}$/i.test(value)
  }

  return /^[a-z0-9][a-z0-9-]{0,99}$/i.test(value)
}

// --------------

function readSocialUrlForPlatform(platform: MarketingSocialPlatform, value: string) {
  const url = readUrlWithOptionalScheme(value)

  if (!url) {
    return ''
  }

  try {
    const parsed = new URL(url)
    const hostname = parsed.hostname.toLowerCase()

    return SOCIAL_HOSTS[platform].has(hostname) ? parsed.toString() : ''
  } catch {
    return ''
  }
}

// --------------

function normalizeLinkedInUrl(value: string) {
  const trimmed = value.trim()
  const detectedUrl = readUrlWithOptionalScheme(trimmed)
  const existingUrl = readSocialUrlForPlatform('linkedin', trimmed)

  if (detectedUrl || existingUrl) {
    return existingUrl
  }

  const username = normalizeSocialUsername(trimmed)
  if (!username) {
    return ''
  }

  const pathParts = username.split('/')
  const [pathType, pathHandle] = pathParts
  const normalizedPathType = pathType?.toLowerCase()

  if (
    (normalizedPathType === 'company' || normalizedPathType === 'in') &&
    pathHandle &&
    pathParts.length === 2
  ) {
    return isValidSocialHandle('linkedin', pathHandle)
      ? `https://www.linkedin.com/${normalizedPathType}/${pathHandle}`
      : ''
  }

  if (username.includes('/')) {
    return ''
  }

  return isValidSocialHandle('linkedin', username)
    ? `https://www.linkedin.com/company/${username}`
    : ''
}

// --------------

export function normalizeSocialUrl(platform: MarketingSocialPlatform, value: string) {
  if (platform === 'linkedin') {
    return normalizeLinkedInUrl(value)
  }

  const trimmed = value.trim()
  const detectedUrl = readUrlWithOptionalScheme(trimmed)
  const existingUrl = readSocialUrlForPlatform(platform, trimmed)

  if (detectedUrl || existingUrl) {
    return existingUrl
  }

  const username = normalizeSocialUsername(trimmed)
  if (!username || !isValidSocialHandle(platform, username)) {
    return ''
  }

  const host = platform === 'instagram' ? 'instagram.com' : 'x.com'
  return `https://${host}/${username}`
}
